import React, { useState, useEffect } from 'react';
import {
    makeStyles, withStyles, Grid, TextField, MenuItem, Typography, 
    Zoom, CircularProgress, Paper, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Button
} from '@material-ui/core';
import { authFetch, logout } from '../../auth';

const useStyles = makeStyles((theme) => ({
    root: { padding: theme.spacing(3) },
    sectionTitle: {
        marginBottom: 10,
        backgroundColor: theme.palette.secondary.light,
        borderRadius: 3,
        color: theme.palette.primary.dark,
        padding: '5px 10px'
    },
    customFilled: { backgroundColor: '#f5f5f5' },
    headerSection: {
        backgroundColor: theme.palette.primary.dark,
        color: 'white',
        fontWeight: 'bold',
        textAlign: 'center',
        border: '1px solid rgba(224, 224, 224, 1)'
    }
}));

const StyledTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontSize: '0.75rem',
        padding: '4px'
    },
    body: { fontSize: 13, padding: '4px 8px' },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
    root: {
        '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
    },
}))(TableRow);

const EnergyCalculationComponent = (props) => {
    const classes = useStyles();
    
    const [selectedScheme, setSelectedScheme] = useState('individual');
    const [selectedClient, setSelectedClient] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isCalculating, setIsCalculating] = useState(false);
    const [cycles, setCycles] = useState([]);
    const [selectedCycle, setSelectedCycle] = useState("");
    const [compensationUsers, setCompensationUsers] = useState([]);

    const fileInputRef = React.useRef(null);
    const [activeRowIndex, setActiveRowIndex] = useState(null);

    const [tableData, setTableData] = useState([]);
    const [historyComplete, setHistoryComplete] = useState(false);
    const [cycleLiqConsumptionFlag, setCycleLiqConsumptionFlag] = useState(false);

    const handleParametrizeClick = (index) => {
        setActiveRowIndex(index);
        fileInputRef.current.click();
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file || activeRowIndex === null) return;

        const dateBase = props.billingDate ? new Date(props.billingDate) : new Date();
        const offset = 12 - activeRowIndex;
        const targetDate = new Date(dateBase.getFullYear(), dateBase.getMonth() - offset, 1);
        
        const year = targetDate.getFullYear();
        const month = String(targetDate.getMonth() + 1).padStart(2, '0');
        const formattedDate = `${year}-${month}-01`;

        const formData = new FormData();
        formData.append('file', file);

        setIsLoading(true);
        try {
            const response = await authFetch(
                `/api/process-compensation-file?username=${props.username}&client_code=${selectedClient}&date=${formattedDate}`,
                {
                    method: 'POST',
                    body: formData,
                }
            );

            if (response.ok) {
                alert("Archivo procesado correctamente");
                await fetchTableParams(selectedClient);
            } else {
                alert("Error al procesar el archivo");
            }
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setIsLoading(false);
            event.target.value = null; 
            setActiveRowIndex(null);
        }
    };

    const fetchTableParams = async (clientCode) => {
        if (!clientCode || !props.billingDate) return;
        let year = props.billingDate.getFullYear();
        let monthIndex = props.billingDate.getMonth() + 1; 
        const formattedDate = `${year}-${String(monthIndex).padStart(2, '0')}-01`;
        
        setIsLoading(true);
        try {
            const response = await authFetch(
                `/api/read-user-compensation-params?username=${props.username}&client_code=${clientCode}&period=${formattedDate}`
            );
            const data = await response.json();
            if (data.history) {
                setTableData(data.history);
                setHistoryComplete(data.complete_history);
            }
        } catch (error) {
            console.error("Error al leer parámetros:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCalculate = async () => {
        if (!selectedClient || !props.billingDate) return;

        const year = props.billingDate.getFullYear();
        const month = String(props.billingDate.getMonth() + 1).padStart(2, '0');
        const formattedDate = `${year}-${month}-01`;

        setIsCalculating(true);
        try {
            const response = await authFetch(
                `/api/calc-user-compensation?client_code=${selectedClient}&billingDate=${formattedDate}&username=${props.username}`,
                { method: 'POST' }
            );

            if (response.ok) {
                alert("Cálculo realizado con éxito. Los indicadores han sido actualizados.");
                await fetchTableParams(selectedClient);
            } else {
                const err = await response.json();
                alert("Error en el cálculo: " + (err.message || "Consulte los logs del servidor"));
            }
        } catch (error) {
            console.error("Error:", error);
            alert("Error de conexión al servidor");
        } finally {
            setIsCalculating(false);
        }
    };

    const paramFields = ['DIUG', 'DIUM', 'FIUG', 'FIUM', 'EXCT'];
    const calcFields = ['DIU', 'FIU', 'THC', 'HC', 'TVC', 'VC', '%T', 'Dt', 'CEC', 'VCD', 'VCF'];

    const getRollingWindow = (baseDateStr) => {
        const months = [];
        const date = baseDateStr ? new Date(baseDateStr) : new Date();
        for (let i = 12; i >= 1; i--) {
            const d = new Date(date.getFullYear(), date.getMonth() - i, 1);
            months.push(d.toLocaleString('es-ES', { month: 'long', year: 'numeric' }));
        }
        return months;
    };

    const rollingMonths = getRollingWindow(props.billingDate);

    useEffect(() => {
        document.title = "Parametrización y cálculo de compensación";
        authFetch("/api/check-billing-cycles?username=" + String(props.username) + "&user_type=" + props.userType)
            .then(r => r.status === 422 ? logout() : r.json())
            .then(response => { if (response?.cycles) setCycles(response.cycles); });        
    }, [props.billingDate]);

    useEffect(() => {
        if (selectedCycle !== "") {
            authFetch("/api/check-compensation-users?username=" + props.username + "&cycle=" + selectedCycle)
                .then(r => r.status === 422 ? logout() : r.json())
                .then(response => { if (response?.compensation_users) setCompensationUsers(response.compensation_users); });
            
            // Lógica de flags de liquidación
            authFetch("/api/get-cycle-users?username=" + String(props.username) + "&billing=true")
            .then(r => r.status === 422 ? logout() : r.json())
            .then(response => {
                if (response?.cycle_users) {
                    let opts = {
                        users_by_cycle: response.cycle_users,
                        year: props.billingDate.getFullYear(),
                        month: String(props.billingDate.getMonth() + 1)
                    };
                    authFetch("/api/check-consumption-liquidation-flags?username=" + String(props.username) +
                        "&year=" + opts.year + "&month=" + opts.month,
                        { method: 'post', body: JSON.stringify(opts) }
                    ).then(r => r.json())
                    .then(res => {
                        if (res?.consumption_liq_flags) {
                            setCycleLiqConsumptionFlag(res.consumption_liq_flags[selectedCycle]);
                        }
                    });
                }
            });
        }
    }, [selectedCycle, props.billingDate]);

    useEffect(() => {
        if (selectedClient) {
            fetchTableParams(selectedClient);
        }
    }, [selectedClient, props.billingDate]);

    return (
        <div className={classes.root}>
            <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".csv" onChange={handleFileChange} />
            <Zoom in timeout={800}>
                <Grid container alignItems="center" justify='center' spacing={3}>
                    <Grid item xs={11}>
                        <Typography variant="subtitle1" className={classes.sectionTitle}>Indicadores de Calidad y Compensaciones</Typography>
                    </Grid>
                    <Grid item xs={4}></Grid>
                    <Grid item xs={3}>
                        <Typography variant="subtitle2" align="center" color="primary">Ciclo:</Typography>
                        <TextField select variant="outlined" size="small" value={selectedCycle} fullWidth onChange={(e) => setSelectedCycle(e.target.value)}>
                            {cycles.map((cycle, idx) => (<MenuItem key={idx} value={cycle.code}>{cycle.code}</MenuItem>))}
                        </TextField>
                    </Grid>
                    <Grid item xs={4}></Grid>

                    

                    <Grid item xs={11} md={5}>
                        <TextField select fullWidth label="Tipo de Esquema de Calidad" variant="filled" size="small" className={classes.customFilled} value={selectedScheme} onChange={(e) => setSelectedScheme(e.target.value)}>
                            <MenuItem value="individual">Esquema de Calidad Individual</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={11} md={5}>
                        <TextField select fullWidth label="Seleccionar Cliente" variant="filled" size="small" className={classes.customFilled} value={selectedClient} onChange={(e) => setSelectedClient(e.target.value)}>
                            {compensationUsers.map((client, idx) => (
                                <MenuItem key={idx} value={client.client_code}>{client.client_code} - {client.frontier_name}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        {selectedScheme === 'individual' && selectedClient ? (
                            <TableContainer component={Paper} elevation={3}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell colSpan={2}></StyledTableCell>
                                            <StyledTableCell colSpan={5} className={classes.headerSection}>PARAMETRIZACIÓN</StyledTableCell>
                                            <StyledTableCell colSpan={11} className={classes.headerSection}>CÁLCULO</StyledTableCell>
                                            <StyledTableCell colSpan={1}></StyledTableCell>
                                        </TableRow>
                                        <TableRow>
                                            <StyledTableCell align="center">Cliente</StyledTableCell>
                                            <StyledTableCell align="center">Periodo</StyledTableCell>
                                            {paramFields.concat(calcFields).map(field => (
                                                <StyledTableCell key={field} align="center">{field}</StyledTableCell>
                                            ))}
                                            <StyledTableCell align="center">Acciones</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {isLoading ? (
                                            <TableRow><TableCell colSpan={20} align="center"><CircularProgress size={24} style={{margin: '20px'}} /></TableCell></TableRow>
                                        ) : (
                                            rollingMonths.map((period, index) => {
                                                const row = tableData[index] || {};
                                                return (
                                                    <StyledTableRow key={index}>
                                                        <StyledTableCell align="center" style={{ fontWeight: 'bold' }}>{selectedClient}</StyledTableCell>
                                                        <StyledTableCell align="center" style={{ textTransform: 'capitalize' }}>{period}</StyledTableCell>
                                                        <StyledTableCell align="center">{row.diug || 0}</StyledTableCell>
                                                        <StyledTableCell align="center">{row.dium || 0}</StyledTableCell>
                                                        <StyledTableCell align="center">{row.fiug || 0}</StyledTableCell>
                                                        <StyledTableCell align="center">{row.fium || 0}</StyledTableCell>
                                                        <StyledTableCell align="center">{row.exct || 0}</StyledTableCell>
                                                        
                                                        <StyledTableCell align="center">{row.diu || 0}</StyledTableCell>
                                                        <StyledTableCell align="center">{row.fiu || 0}</StyledTableCell>
                                                        <StyledTableCell align="center">{row.thc || 0}</StyledTableCell>
                                                        <StyledTableCell align="center">{row.hc || 0}</StyledTableCell>
                                                        <StyledTableCell align="center">{row.tvc || 0}</StyledTableCell>
                                                        <StyledTableCell align="center">{row.vc || 0}</StyledTableCell>
                                                        <StyledTableCell align="center">{row.perc_t || 0}</StyledTableCell>
                                                        <StyledTableCell align="center">{row.dt || 0}</StyledTableCell>
                                                        <StyledTableCell align="center">{row.cec || 0}</StyledTableCell>
                                                        <StyledTableCell align="center">{row.vcd || 0}</StyledTableCell>
                                                        <StyledTableCell align="center">{row.vcf || 0}</StyledTableCell>

                                                        <StyledTableCell align="center">
                                                            <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                                                                <Button size="small" variant="contained" color="default" style={{fontSize: '10px'}} onClick={() => handleParametrizeClick(index)}>
                                                                    Parametrizar
                                                                </Button>
                                                                {index === 11 && (
                                                                    <Button 
                                                                        size="small" 
                                                                        variant="contained" 
                                                                        color="primary" 
                                                                        style={{fontSize: '10px'}} 
                                                                        onClick={handleCalculate}
                                                                        disabled={!historyComplete || !cycleLiqConsumptionFlag || isCalculating}
                                                                    >
                                                                        {isCalculating ? <CircularProgress size={14} color="inherit" /> : "Calcular"}
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                )
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Paper style={{ padding: '20px', textAlign: 'center' }}>
                                <Typography variant="body1" color="textSecondary">Seleccione un cliente para visualizar la ventana móvil.</Typography>
                            </Paper>
                        )}
                    </Grid>
                </Grid>
            </Zoom>
        </div>
    );
};

export default EnergyCalculationComponent;